import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useInvite } from "@/hooks/useInvite";
import { CandidateFlow } from "@/shell/CandidateFlow";
import { Button, Spinner } from "@/components/primitives";
import { InviteError } from "./InviteError";

// C4 — Personalisation quiz (3+1 questions). Answers stored in sessionStorage
// and read by SessionReveal (C5) to show the tailored session plan.

interface PersonaliseData {
  concern: string;
  practiceStyle: string;
  prepLevel: string;
  timeUntilInterview: string;
}

const CONCERN_OPTIONS = [
  { value: "narrative", label: "Telling my career story convincingly" },
  { value: "tough", label: "Handling tough or unexpected questions" },
  { value: "seniority", label: "Coming across at the right level of seniority" },
  { value: "closing", label: "Closing the conversation well" },
];

const STYLE_OPTIONS = [
  { value: "structured", label: "Give me structured feedback after each answer" },
  { value: "free", label: "Let me practise freely, then review at the end" },
  { value: "both", label: "Both" },
];

const PREP_OPTIONS = [
  { value: "fresh", label: "Not yet — starting fresh" },
  { value: "reading", label: "I've done some reading" },
  { value: "practised", label: "I've practised with a person already" },
];

const TIME_OPTIONS = [
  { value: "48h", label: "Under 48 hours" },
  { value: "week", label: "3–7 days" },
  { value: "twoweeks", label: "1–2 weeks" },
  { value: "more", label: "More than 2 weeks" },
];

function RadioGroup({
  name,
  options,
  value,
  onChange,
}: {
  name: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      {options.map((opt) => (
        <label
          key={opt.value}
          className={`flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3 transition-colors ${
            value === opt.value
              ? "border-accent bg-accent-50"
              : "border-hair bg-cream hover:border-slate-300"
          }`}
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            className="h-4 w-4 shrink-0 accent-accent"
          />
          <span className="text-sm text-ink-2">{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

export function Personalise() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { data, error, loading } = useInvite(token);

  const [concern, setConcern] = useState("");
  const [practiceStyle, setPracticeStyle] = useState("");
  const [prepLevel, setPrepLevel] = useState("");
  const [timeUntil, setTimeUntil] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  function submit() {
    if (!concern || !practiceStyle || !prepLevel || !timeUntil) {
      setFormError("Please answer all four questions to continue.");
      return;
    }
    const answers: PersonaliseData = {
      concern,
      practiceStyle,
      prepLevel,
      timeUntilInterview: timeUntil,
    };
    try {
      sessionStorage.setItem("ic_personalise", JSON.stringify(answers));
    } catch {
      // sessionStorage may be blocked — proceed anyway
    }
    navigate(`/invite/${token}/reveal`);
  }

  if (loading) {
    return (
      <CandidateFlow>
        <div className="flex justify-center py-10"><Spinner size={28} /></div>
      </CandidateFlow>
    );
  }
  if (error || !data) return <InviteError message={error ?? "Invite not found."} />;

  return (
    <CandidateFlow
      agencyName={data.agencyName}
      logoUrl={data.branding?.logoUrlLight || undefined}
      logoFilter={data.branding?.logoFilter}
      width="wide"
    >
      <Link to={`/invite/${token}`} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-ink">
        <span aria-hidden="true">←</span> Back
      </Link>

      <div className="mb-6">
        <p className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-accent">
          Tailoring your session
        </p>
        <h1 className="text-xl font-semibold text-ink">Let us personalise your session</h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-500">
          Four questions — takes 45 seconds. We use your answers to set the right intensity, focus
          areas, and feedback style.
        </p>
      </div>

      {formError && (
        <div className="mb-4 rounded-md border border-crit bg-[#f7e9e6] px-4 py-3 text-sm text-crit">
          {formError}
        </div>
      )}

      <div className="space-y-7">
        <div>
          <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 pb-2 border-b border-hair">
            What is your biggest interview concern?
          </p>
          <RadioGroup
            name="concern"
            options={CONCERN_OPTIONS}
            value={concern}
            onChange={setConcern}
          />
        </div>

        <div>
          <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 pb-2 border-b border-hair">
            How do you prefer to practise?
          </p>
          <RadioGroup
            name="practiceStyle"
            options={STYLE_OPTIONS}
            value={practiceStyle}
            onChange={setPracticeStyle}
          />
        </div>

        <div>
          <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 pb-2 border-b border-hair">
            Have you prepared for this interview already?
          </p>
          <RadioGroup
            name="prepLevel"
            options={PREP_OPTIONS}
            value={prepLevel}
            onChange={setPrepLevel}
          />
        </div>

        <div>
          <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 pb-2 border-b border-hair">
            How long until your interview?
          </p>
          <RadioGroup
            name="timeUntil"
            options={TIME_OPTIONS}
            value={timeUntil}
            onChange={setTimeUntil}
          />
        </div>
      </div>

      <div className="mt-8">
        <Button variant="accent" className="w-full" onClick={submit}>
          See your personalised session plan →
        </Button>
      </div>
    </CandidateFlow>
  );
}
